import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { api } from '@/lib/api';
import {
  Badge,
  Card,
  EmptyState,
  ErrorState,
  Loading,
  PageHeader,
  Select,
  StatCard,
} from '@/components/ui';
import { formatDate, formatDuration, formatNumber, formatPercent } from '@/lib/format';
import { SERIES, axisProps, gridProps, tooltipProps } from '@/lib/chartTheme';

const WINDOWS = [
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
];

interface ClientRow {
  tenant_id: string;
  client_name: string;
  slug: string;
  status: string;
  billing_model: string;
  max_bots: number;
  calls: number;
  qualified: number;
  transferred: number;
  seconds: number;
  bots_in_use: number;
}

interface Stats {
  window_days: number;
  summary: {
    calls: number;
    qualified: number;
    transferred: number;
    dnc: number;
    not_interested: number;
    failed: number;
    seconds: number;
    active_clients: number;
    qualification_rate: number;
    transfer_rate: number;
    avg_seconds: number;
  };
  clients: ClientRow[];
  daily: Array<{ day: string; calls: number; qualified: number }>;
}

const BILLING_LABELS: Record<string, string> = {
  MULTIPLIER: 'Cost × multiplier',
  MRC_PER_BOT: 'Monthly per bot',
  PER_CALL: 'Per call',
  HYBRID: 'Hybrid',
};

/** Platform-wide call and outcome statistics, and the same figures per client. */
export default function Stats() {
  const [days, setDays] = useState(30);

  const query = useQuery({
    queryKey: ['admin-stats', days],
    queryFn: () => api.get<Stats>('/admin/stats', { days }),
  });

  if (query.isPending) return <Loading />;
  if (query.error) return <ErrorState error={query.error} retry={() => query.refetch()} />;

  const { summary, clients, daily } = query.data;

  return (
    <div className="w-full">
      <PageHeader
        title="Statistics"
        description="Call volume and outcomes across every client."
        actions={
          <Select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="w-auto"
          >
            {WINDOWS.map((w) => (
              <option key={w.days} value={w.days}>
                Last {w.label}
              </option>
            ))}
          </Select>
        }
      />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard
          label="Calls"
          value={formatNumber(summary.calls)}
          sub={`${summary.active_clients} client${summary.active_clients === 1 ? '' : 's'} active`}
        />
        <StatCard
          label="Qualified"
          value={formatNumber(summary.qualified)}
          sub={`${formatPercent(summary.qualification_rate)} of calls`}
          tone="good"
        />
        <StatCard
          label="Transferred"
          value={formatNumber(summary.transferred)}
          sub={`${formatPercent(summary.transfer_rate)} of calls`}
        />
        <StatCard
          label="Average call"
          value={formatDuration(summary.avg_seconds)}
          sub={`${formatDuration(summary.seconds)} total`}
        />
      </div>

      {/* Outcomes that are not successes still need to be visible: a rising DNC
          count is the earliest sign that a list or a script is a problem. */}
      <div className="mt-4 grid grid-cols-2 gap-4 lg:grid-cols-3">
        <StatCard label="Not interested" value={formatNumber(summary.not_interested)} />
        <StatCard
          label="Do not call"
          value={formatNumber(summary.dnc)}
          tone={summary.dnc > 0 ? 'warn' : 'default'}
        />
        <StatCard
          label="Failed to start"
          value={formatNumber(summary.failed)}
          tone={summary.failed > 0 ? 'bad' : 'default'}
          sub={summary.failed > 0 ? 'Usually no bot assigned' : undefined}
        />
      </div>

      <Card
        title="Volume over time"
        description="Calls and qualified leads per day."
        className="mt-6"
      >
        {daily.length === 0 ? (
          <p className="py-10 text-center text-sm text-ink-500">
            No calls in this window.
          </p>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={daily} margin={{ left: -18, right: 8, top: 4 }}>
              <defs>
                <linearGradient id="calls" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={SERIES.primary} stopOpacity={0.28} />
                  <stop offset="100%" stopColor={SERIES.primary} stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="qualified" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={SERIES.success} stopOpacity={0.3} />
                  <stop offset="100%" stopColor={SERIES.success} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid {...gridProps} />
              <XAxis
                dataKey="day"
                tickFormatter={(value) => formatDate(value)}
                {...axisProps}
                minTickGap={24}
              />
              <YAxis
                {...axisProps}
                width={52}
              />
              <Tooltip
                labelFormatter={(value) => formatDate(String(value))}
                {...tooltipProps}
              />
              <Area
                type="monotone"
                dataKey="calls"
                name="Calls"
                stroke={SERIES.primary}
                strokeWidth={2}
                fill="url(#calls)"
              />
              <Area
                type="monotone"
                dataKey="qualified"
                name="Qualified"
                stroke={SERIES.success}
                strokeWidth={2}
                fill="url(#qualified)"
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </Card>

      <Card title="By client" className="mt-6" padded={false}>
        {clients.length === 0 ? (
          <EmptyState title="No clients yet" description="Create one to get started." />
        ) : (
          <div className="scroll-x">
            <table className="glee-table">
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Billing</th>
                  <th className="text-right">Bots</th>
                  <th className="text-right">Calls</th>
                  <th className="text-right">Qualified</th>
                  <th className="text-right">Rate</th>
                  <th className="text-right">Talk time</th>
                </tr>
              </thead>
              <tbody>
                {clients.map((row) => {
                  const rate = row.calls ? (row.qualified / row.calls) * 100 : 0;
                  // A client at their ceiling cannot add another bot without
                  // someone raising it, so it is worth seeing before they ask.
                  const atCapacity = row.bots_in_use >= row.max_bots;
                  return (
                    <tr key={row.tenant_id}>
                      <td>
                        <Link
                          to={`/clients/${row.tenant_id}`}
                          className="font-medium text-ink-900 hover:text-brand-600 dark:text-ink-50 dark:hover:text-brand-400"
                        >
                          {row.client_name}
                        </Link>
                        {row.status !== 'ACTIVE' && (
                          <Badge tone={row.status === 'TRIAL' ? 'amber' : 'gray'} className="ml-2">
                            {row.status}
                          </Badge>
                        )}
                      </td>
                      <td className="text-xs text-ink-600 dark:text-ink-300">
                        {BILLING_LABELS[row.billing_model] ?? row.billing_model}
                      </td>
                      <td className="text-right text-xs tabular-nums">
                        <span className={atCapacity ? 'font-semibold text-amber-600 dark:text-amber-400' : 'text-ink-600 dark:text-ink-300'}>
                          {row.bots_in_use}/{row.max_bots}
                        </span>
                      </td>
                      <td className="text-right tabular-nums text-ink-900 dark:text-ink-50">
                        {formatNumber(row.calls)}
                      </td>
                      <td className="text-right tabular-nums text-ink-700 dark:text-ink-200">
                        {formatNumber(row.qualified)}
                      </td>
                      <td className="text-right tabular-nums text-ink-700 dark:text-ink-200">
                        {formatPercent(rate)}
                      </td>
                      <td className="text-right text-xs tabular-nums text-ink-500">
                        {formatDuration(row.seconds)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
